import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  CSP_NONCE,
  provideAppInitializer,
  provideZoneChangeDetection,
  ErrorHandler
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { primeConfig } from '@cfg/design/prime.config';
import { initializePrimeTranslation } from '@cfg/i18n/initializer';
import { translationConfig } from '@cfg/i18n/translation.config';
import { SecurityLoaders } from '@cfg/security/loaders';
import { insertCSP } from '@cfg/security/csp';
import { APP_ROUTES } from '@view/routes';
import { AppComponent } from '@view/app.component';
import { GlobalErrorHandlerService } from './app/core/error-handling/global-error-handler.service';
import { environment } from '@env/environment';
import { LibraryRepository as LibraryRepositoryOld } from './app/domain/service/library/library-repository';
import { HttpLibraryRepository } from './app/infrastructure/adapters/library/http-library.repository';
import { ExtractionSampleRepository } from './app/domain/service/extraction-sample/extraction-sample.repository';
import { CatalogRepository } from './app/domain/service/catalog/catalog.repository';
import { ManualExtractionRepository } from './app/domain/service/associate-extraction/manual/manual-extraction.repository';
import { HttpExtractionSampleRepository } from './app/infrastructure/adapters/extraction-sample/http-extraction-sample.repository';
import { HttpCatalogRepository } from './app/infrastructure/adapters/catalog/http-catalog.repository';
import { HttpManualExtractionRepository } from './app/infrastructure/adapters/associate-extraction/manual/http-manual-extraction.repository';
import { EditExtractionRepository } from './app/domain/service/associate-extraction/edit/edit-extraction.repository';
import { HttpEditExtractionRepository } from './app/infrastructure/adapters/associate-extraction/edit/http-edit-extraction.repository';
import { SampleSelectionRepository } from './app/domain/service/sample-selection/sample-selection.repository';
import { HttpSampleSelectionRepository } from './app/infrastructure/adapters/sample-selection/http-sample-selection.repository';
import { GetSamplesUseCase } from './app/domain/usecase/laboratory/listwork/get-samples.usecase';
import { errorInterceptorService } from '@adapter/security/error-interceptor.service';
import { PATIENT_PORT } from './app/domain/service/preclinical/patient.port';
import { MASTER_DATA_PORT } from './app/domain/service/preclinical/master-data.port';
import { ORDERS_PORT } from './app/domain/service/preclinical/orders.port';
import { RECEIVED_ORDERS_PORT } from './app/domain/service/preclinical/orders.port';
import { ORDERS_EXPORT_PORT } from './app/domain/service/preclinical/orders-export.port';
import { METRICS_PORT } from './app/domain/service/preclinical/metrics.port';
import { FAMILY_MEMBERS_PORT } from './app/domain/service/family-members.port';
import { PatientAdapter } from './app/infrastructure/adapters/patient.adapter';
import { MasterDataAdapter } from './app/infrastructure/adapters/master-data.adapter';
import {
  IngestedOrdersAdapter,
  ReceivedOrdersAdapter
} from './app/infrastructure/adapters/orders.adapter';
import { OrdersExportAdapter } from './app/infrastructure/adapters/orders-export.adapter';
import { MetricsAdapter } from './app/infrastructure/adapters/metrics.adapter';
import { LibraryRepository as LaboratoryLibraryRepository } from './app/domain/service/laboratory/library/library-repository';
import { LibraryRepositoryAdapter } from './app/infrastructure/adapters/worklist/library-repository.adapter';
import { CreateLibraryUseCase } from './app/domain/usecase/laboratory/worklist/create-library.usecase';
import { FileUploadRepository } from './app/domain/service/laboratory/file/file-upload-repository';
import { FileUploadRepositoryAdapter } from './app/infrastructure/adapters/file/file-upload-repository.adapter';
import { FileLibraryRepository } from './app/domain/service/laboratory/file-library/file-library-repository';
import { FileLibraryRepositoryAdapter } from './app/infrastructure/adapters/file-library/file-library-repository.adapter';
import { ExtractionFileRepository } from './app/domain/service/associate-extraction/file/extraction-file.repository';
import { ExtractionFileRepositoryAdapter } from './app/infrastructure/adapters/associate-extraction/file/extraction-file-repository.adapter';
import { SequencingRunRepository } from './app/domain/service/sequencing/sequencing-run-repository';
import { HttpSequencingRunRepository } from './app/infrastructure/adapters/sequencing/http-sequencing-run.repository';
import { LgValidationRepository } from './app/domain/service/lg-validation/lg-validation.repository';
import { HttpLgValidationRepository } from './app/infrastructure/adapters/lg-validation/http-lg-validation.repository';
import { ExtractionPreviewRepository } from './app/domain/service/extraction-preview/extraction-preview.repository';
import { HttpExtractionPreviewRepository } from './app/infrastructure/adapters/extraction-preview/http-extraction-preview.repository';
import { WorklistsStorageGateway } from './app/domain/service/laboratory/worklist/worklists-storage.gateway';
import { WorklistsMemoryStorageAdapter } from './app/infrastructure/adapters/worklist/worklists-memory-storage.adapter';
import { WorklistsLocalStorageUsecase } from './app/domain/usecase/laboratory/worklist/worklists-local-storage.usecase';
import { TRACEABILITY_QUERY_PORT } from './app/domain/service/traceability/traceability-query.port';
import { TraceabilityQueryAdapter } from './app/infrastructure/services/traceability/adapter/traceability-query.adapter';
import { TRACEABILITY_COMMAND_PORT } from './app/domain/service/traceability/traceability-command.port';
import { TraceabilityCommandAdapter } from './app/infrastructure/services/traceability/adapter/traceability-command.adapter';
import { UserSignatureRepository } from './app/domain/service/signature/user-signature.repository';
import { HttpUserSignatureRepository } from './app/infrastructure/adapters/signature/http-user-signature.repository';
import { FamilyMembersService } from './app/infrastructure/services/family-members/family-members.service';
import { TemplateRepository } from './app/domain/service/templates/template.repository';
import { HttpTemplateRepository } from './app/infrastructure/adapters/templates/http-template.repository';
import { ResultTypeRepository } from './app/domain/service/templates/result-type.repository';
import { HttpResultTypeRepository } from './app/infrastructure/adapters/templates/http-result-type.repository';

// Aplicar Content Security Policy solo en ambientes desplegados (no en desarrollo local)
let cspNonce = '';
if (environment.cspDomains?.length > 0) {
  cspNonce = insertCSP(environment.cspDomains);
}

bootstrapApplication(AppComponent, {
  providers: [
    ...(cspNonce ? [{ provide: CSP_NONCE, useValue: cspNonce }] : []),
    provideRouter(APP_ROUTES),
    provideHttpClient(
      withInterceptors([
        ...SecurityLoaders.loadInterceptors(),
        errorInterceptorService
      ])
    ),
    provideAnimations(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAppInitializer(() => initializePrimeTranslation()),
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    // Domain repositories - Dependency Inversion Principle
    { provide: LibraryRepositoryOld, useClass: HttpLibraryRepository },
    {
      provide: ExtractionSampleRepository,
      useClass: HttpExtractionSampleRepository
    },
    {
      provide: CatalogRepository,
      useClass: HttpCatalogRepository
    },
    {
      provide: ManualExtractionRepository,
      useClass: HttpManualExtractionRepository
    },
    {
      provide: EditExtractionRepository,
      useClass: HttpEditExtractionRepository
    },
    {
      provide: SampleSelectionRepository,
      useClass: HttpSampleSelectionRepository
    },
    {
      provide: LaboratoryLibraryRepository,
      useClass: LibraryRepositoryAdapter
    },
    { provide: FileUploadRepository, useClass: FileUploadRepositoryAdapter },
    { provide: FileLibraryRepository, useClass: FileLibraryRepositoryAdapter },
    { provide: SequencingRunRepository, useClass: HttpSequencingRunRepository },
    { provide: UserSignatureRepository, useClass: HttpUserSignatureRepository },
    {
      provide: LgValidationRepository,
      useClass: HttpLgValidationRepository
    },
    {
      provide: LaboratoryLibraryRepository,
      useClass: LibraryRepositoryAdapter
    },
    {
      provide: FileUploadRepository,
      useClass: FileUploadRepositoryAdapter
    },
    {
      provide: FileLibraryRepository,
      useClass: FileLibraryRepositoryAdapter
    },
    {
      provide: ExtractionFileRepository,
      useClass: ExtractionFileRepositoryAdapter
    },
    {
      provide: SequencingRunRepository,
      useClass: HttpSequencingRunRepository
    },
    {
      provide: ExtractionPreviewRepository,
      useClass: HttpExtractionPreviewRepository
    },
    GetSamplesUseCase,
    {
      provide: PATIENT_PORT,
      useClass: PatientAdapter
    },
    {
      provide: MASTER_DATA_PORT,
      useClass: MasterDataAdapter
    },
    {
      provide: ORDERS_PORT,
      useClass: IngestedOrdersAdapter
    },
    {
      provide: RECEIVED_ORDERS_PORT,
      useClass: ReceivedOrdersAdapter
    },
    {
      provide: ORDERS_EXPORT_PORT,
      useClass: OrdersExportAdapter
    },
    {
      provide: METRICS_PORT,
      useClass: MetricsAdapter
    },
    {
      provide: TRACEABILITY_QUERY_PORT,
      useClass: TraceabilityQueryAdapter
    },
    {
      provide: TRACEABILITY_COMMAND_PORT,
      useClass: TraceabilityCommandAdapter
    },
    {
      provide: FAMILY_MEMBERS_PORT,
      useClass: FamilyMembersService
    },
    {
      provide: WorklistsStorageGateway,
      useClass: WorklistsMemoryStorageAdapter
    },
    WorklistsLocalStorageUsecase,
    CreateLibraryUseCase,
    // Templates module - DIP bindings
    { provide: TemplateRepository, useClass: HttpTemplateRepository },
    { provide: ResultTypeRepository, useClass: HttpResultTypeRepository },
    ...primeConfig,
    ...translationConfig
  ]
}).catch((err) => console.error(err));
